import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RbacApi } from '@/services/school.api';
import { useAuthStore } from '@/stores/auth.store';
import { AccessCtx, PermAction, can as canFn, isAdminRole } from '@/lib/access';

/**
 * The logged-in user's effective role + permission set (from /school/me),
 * with helpers. Falls back to the auth-store role while loading so admins
 * aren't briefly locked out.
 *
 * `/school/me` is a TENANT-scoped endpoint — calling it from an org-tier
 * session with no school selected yet trips the axios interceptor's
 * `SCHOOL_NOT_SELECTED` handling, which force-navigates the whole browser
 * to `/org/select-school`. Pass `enabled: false` from any component that
 * might render outside a school-tenant session (shared org/school pages)
 * so the query never fires there.
 */
export function usePermissions(enabled = true) {
  const role = useAuthStore((s) => s.user?.role);
  const { data } = useQuery({
    queryKey: ['me-permissions'],
    queryFn: RbacApi.me,
    staleTime: 2 * 60 * 1000,
    retry: false,
    enabled,
  });

  return useMemo(() => {
    const isAdmin = data?.isAdmin ?? isAdminRole(role);
    const permissions = new Set(data?.permissions ?? []);
    const ctx: AccessCtx = { isAdmin, permissions };
    return {
      isAdmin,
      permissions,
      ctx,
      role: data?.role ?? role,
      isSystem: data?.isSystem ?? true,
      loaded: !!data,
      can: (moduleKey: string, action: PermAction) =>
        canFn(ctx, moduleKey, action),
    };
  }, [data, role]);
}
